/**
 * SKI AXIS · /axis-index/cohort1 — Cohort 1 Internal Bronze Holding Page
 *
 * Purpose: A gated landing page for Cohort 1 founders arriving from the
 * July 7, 2026 intake email. The public Bronze questionnaire launches Q1 2027,
 * but Cohort 1 is auto-indexed at intake — this page explains the schedule,
 * what they will be asked, and what their first scorecard will look like.
 *
 * No assessment instrument runs on this page yet. The instrument launches
 * on July 1, 2026 (Sprint 1). This is a holding + orientation page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Calendar, FileCheck2, ShieldCheck, Layers, Cpu, Users, Coins, Lock,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const STYLES = `
.axc-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.axc-shell{max-width:980px;margin:0 auto;padding:64px 32px;}
@media(max-width:680px){.axc-shell{padding:40px 22px;}}

.axc-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:22px;}
.axc-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.axc-h1{font-family:'Cormorant Display',serif;font-size:clamp(40px,5.5vw,68px);font-weight:700;line-height:1.02;letter-spacing:-.02em;margin:0 0 20px;color:#0D1B2A;}
.axc-h1 em{font-style:italic;color:#C8A84B;}
.axc-sub{font-size:18px;line-height:1.55;color:#3D4A5C;max-width:760px;font-weight:300;margin:0 0 36px;}

.axc-card{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:36px 40px;margin-bottom:18px;}
@media(max-width:680px){.axc-card{padding:28px 24px;}}
.axc-card-eb{font-size:11px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:12px;}
.axc-card-h{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;color:#0D1B2A;margin:0 0 12px;line-height:1.2;letter-spacing:-.005em;}
.axc-card-h em{font-style:italic;color:#C8A84B;}
.axc-card-p{font-size:14.5px;color:#3D4A5C;line-height:1.7;margin:0 0 8px;}
.axc-card-p strong{color:#0D1B2A;font-weight:700;}

.axc-timeline{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:18px 0 4px;}
@media(max-width:780px){.axc-timeline{grid-template-columns:1fr 1fr;}}
@media(max-width:480px){.axc-timeline{grid-template-columns:1fr;}}
.axc-tl-stage{background:#FBF8EE;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:18px 16px;}
.axc-tl-when{font-size:10px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:#8A6A14;margin-bottom:6px;}
.axc-tl-name{font-family:'Cormorant Display',serif;font-size:16px;font-weight:700;color:#0D1B2A;line-height:1.18;margin:0 0 4px;letter-spacing:-.005em;}
.axc-tl-d{font-size:11.5px;line-height:1.55;color:#3D4A5C;margin:0;}

.axc-dims-row{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-top:14px;}
@media(max-width:780px){.axc-dims-row{grid-template-columns:repeat(2,1fr);}}
@media(max-width:480px){.axc-dims-row{grid-template-columns:1fr;}}
.axc-dim{background:#FBF8EE;border:1px solid #E8DFC9;padding:14px 12px;text-align:center;}
.axc-dim svg{color:#C8A84B;margin:0 auto 6px;display:block;}
.axc-dim div{font-size:10.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#0D1B2A;}

.axc-rights{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:36px 40px;border-left:4px solid #C8A84B;margin-bottom:18px;}
@media(max-width:680px){.axc-rights{padding:28px 24px;}}
.axc-rights-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:10px;}
.axc-rights-h{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#FAF7F0;margin:0 0 14px;line-height:1.2;letter-spacing:-.005em;}
.axc-rights-h em{font-style:italic;color:#C8A84B;}
.axc-rights ul{margin:0;padding:0;list-style:none;}
.axc-rights li{font-size:13.5px;color:rgba(250,247,240,.85);line-height:1.65;padding:6px 0 6px 22px;position:relative;border-top:1px solid rgba(200,168,75,.16);}
.axc-rights li:first-child{border-top:0;}
.axc-rights li::before{content:'';width:8px;height:8px;background:#C8A84B;border-radius:50%;position:absolute;left:0;top:13px;}

.axc-link-row{display:flex;flex-wrap:wrap;gap:14px;margin-top:36px;align-items:center;}
.axc-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:16px 28px;font-size:11.5px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;border:1px solid #0D1B2A;cursor:pointer;font-family:inherit;transition:all .2s;}
.axc-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.axc-cta-2{display:inline-flex;align-items:center;gap:10px;color:#0D1B2A;padding:16px 24px;font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;border:1px solid rgba(13,27,42,.25);cursor:pointer;font-family:inherit;background:transparent;transition:all .2s;}
.axc-cta-2:hover{border-color:#C8A84B;color:#C8A84B;}
.axc-help{font-size:13px;color:#3D4A5C;font-style:italic;font-family:'Cormorant Display',serif;font-size:15px;}
.axc-help a{color:#0D1B2A;text-decoration:underline;text-underline-offset:3px;}
.axc-help a:hover{color:#C8A84B;}
`;

const TIMELINE = [
  { when: 'Mon · 7 Jul 2026', name: 'Bronze Self-Declaration', d: 'You will receive a private link to The Axis Index Bronze questionnaire. ~30 minutes. No documents required.' },
  { when: 'Wk 1–4', name: 'Confidential Baseline', d: 'Your intake score is held confidentially. Only you, your Cohort Lead, and the panel see it.' },
  { when: 'Wk 6 · ~9 Aug', name: 'Mid-Cohort Check-In', d: 'A short conversation about progress, friction, and the case-study consent question.' },
  { when: 'Wk 12 · 26 Sep', name: 'Silver Re-Index', d: 'Cross-Chapter validated re-assessment at graduation. Your improvement is the headline of the 2027 Annual Report.' },
];

const DIMENSIONS = [
  { icn: <Layers className="h-4 w-4" />, name: 'Institutional Design' },
  { icn: <ShieldCheck className="h-4 w-4" />, name: 'Compliance Architecture' },
  { icn: <Cpu className="h-4 w-4" />, name: 'AI & IP Governance' },
  { icn: <Users className="h-4 w-4" />, name: 'Leadership Systems' },
  { icn: <Coins className="h-4 w-4" />, name: 'Financial Governance' },
];

export default function AxisIndexCohort1Page() {
  useSEO({
    title: 'Cohort 1 · The Axis Index™ Intake | SKI Axis',
    description: 'Cohort 1 founders are auto-indexed at intake (Bronze) and re-indexed at graduation (Silver). Your private intake schedule, dimensions, and rights — held confidentially.',
  });

  return (
    <div className="axc-root" data-testid="axis-index-cohort1-page">
      <style>{STYLES}</style>
      <Header />

      <div className="axc-shell">
        <div className="axc-eb">
          <Lock className="h-3 w-3" /> Cohort 1 · Internal Bronze Track · Confidential
        </div>
        <h1 className="axc-h1">
          Welcome to your <em>Axis Index</em> intake.
        </h1>
        <p className="axc-sub">
          As a Cohort 1 founder, you are <strong>auto-indexed</strong> at intake and re-indexed at graduation —
          at no cost, by invitation only, held confidentially. This page explains the
          schedule, what we ask, and your rights. Nothing is published before October 2026 without
          your explicit written consent.
        </p>

        <div className="axc-card" data-testid="axc-schedule">
          <div className="axc-card-eb"><Calendar className="inline h-3 w-3" style={{ marginRight: 6, marginTop: -2 }} /> Your Schedule</div>
          <h2 className="axc-card-h">Four moments across <em>twelve weeks</em>.</h2>
          <p className="axc-card-p">Two assessments. One mid-cohort conversation. One published narrative — yours, not ours.</p>
          <div className="axc-timeline">
            {TIMELINE.map((t, i) => (
              <div key={i} className="axc-tl-stage" data-testid={`axc-tl-${i}`}>
                <div className="axc-tl-when">{t.when}</div>
                <h3 className="axc-tl-name">{t.name}</h3>
                <p className="axc-tl-d">{t.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="axc-card" data-testid="axc-dimensions">
          <div className="axc-card-eb"><FileCheck2 className="inline h-3 w-3" style={{ marginRight: 6, marginTop: -2 }} /> What You Will Be Asked</div>
          <h2 className="axc-card-h">Five dimensions · <em>thirty minutes</em>.</h2>
          <p className="axc-card-p">
            No documents required at Bronze. Self-declared questionnaire across the five dimensions of
            The Axis Index v1.2 framework. Your stage tag (Pre-Seed · Seed · Series A) is locked at
            assessment and never re-cast. The Floor Rule applies: a high score in one dimension cannot
            mask a low score in another.
          </p>
          <div className="axc-dims-row">
            {DIMENSIONS.map((d) => (
              <div key={d.name} className="axc-dim">{d.icn}<div>{d.name}</div></div>
            ))}
          </div>
        </div>

        <div className="axc-rights" data-testid="axc-rights">
          <div className="axc-rights-eb"><Lock className="inline h-3 w-3" style={{ marginRight: 6, marginTop: -2 }} /> Your Rights · The Founder Charter</div>
          <h2 className="axc-rights-h">You retain agency at <em>every step</em>.</h2>
          <ul>
            <li>Your Bronze and Silver scores are held confidentially through October 2026 and beyond unless you opt in.</li>
            <li>You may withdraw your data from the assessment at any time. No questions, no penalty.</li>
            <li>The 2027 Annual Report uses Cohort 1 data anonymised by default. Named case studies require your separate written consent at Week 6.</li>
            <li>You retain a copy of your scorecard, your evidence, and your improvement deltas — yours to share or not.</li>
            <li>No founder data is shared with third parties (investors, free zones, media) without your explicit written consent.</li>
            <li>Documents you upload are encrypted at rest (AES-256) and in transit (TLS 1.3). Role-restricted access. Assessor NDAs.</li>
          </ul>
        </div>

        <div className="axc-card" data-testid="axc-next">
          <div className="axc-card-eb">What Happens Next</div>
          <h2 className="axc-card-h">The intake link arrives <em>5 July 2026</em>.</h2>
          <p className="axc-card-p">
            On Sunday <strong>5 July 2026</strong> you will receive a private email containing your personal Bronze intake link.
            The questionnaire is web-based, takes ~30 minutes, and can be paused and resumed.
            Please complete it by <strong>Friday 10 July 2026</strong>.
          </p>
          <p className="axc-card-p" style={{ marginTop: 14 }}>
            <strong>Until then, no action is required.</strong> If you have questions about your participation, methodology, or
            consent — your Cohort Lead is the first point of contact, and the framework itself is published openly.
          </p>

          <div className="axc-link-row">
            <Link to="/axis-index" className="axc-cta" data-testid="axc-read-framework">
              Read the v1.2 Framework <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="mailto:hello@skiaxis.com?subject=Cohort%201%20%C2%B7%20Axis%20Index%20Question" className="axc-cta-2" data-testid="axc-email-help">
              Email a question
            </a>
            <span className="axc-help">
              Founders Charter, methodology, and rights — all on <Link to="/axis-index">/axis-index</Link>.
            </span>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
